import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  contentGridApi,
  ContentGridPayload,
  ContentGridSummary,
} from "./services/api";
import { ContentGridRow } from "../../ui/utils/socialContentGridShare";

export const useContentGridCore = () => {
  const [loading, setLoading] = useState(false);

  const listGrids = useCallback(async (companyId: number) => {
    setLoading(true);
    try {
      return await contentGridApi.list(companyId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Error al cargar grillas");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGrid = useCallback(async (companyId: number, gridId?: number) => {
    setLoading(true);
    try {
      if (gridId) return await contentGridApi.get(gridId, companyId);
      return await contentGridApi.getDefault(companyId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Error al cargar grilla");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveRows = useCallback(
    async (gridId: number, companyId: number, rows: ContentGridRow[]) => {
      return contentGridApi.saveRows(gridId, companyId, rows);
    },
    []
  );

  const createGrid = useCallback(
    async (companyId: number, opts?: { name?: string; duplicateFromId?: number }) => {
      setLoading(true);
      try {
        return await contentGridApi.create({ companyId, ...opts });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const addRowsBatch = useCallback(
    async (gridId: number, companyId: number, rows: ContentGridRow[]) => {
      return contentGridApi.addRowsBatch(gridId, companyId, rows);
    },
    []
  );

  const sendToCalendar = useCallback(
    async (
      gridId: number,
      companyId: number,
      rowIds: string[],
      network?: string
    ) => {
      setLoading(true);
      try {
        return await contentGridApi.sendToCalendar(
          gridId,
          companyId,
          rowIds,
          network
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createShareLink = useCallback(
    async (gridId: number, companyId: number) => {
      return contentGridApi.createShareLink(gridId, companyId);
    },
    []
  );

  const uploadMedia = useCallback(
    async (
      gridId: number,
      rowId: string,
      companyId: number,
      file: File,
      mediaType: "image" | "video"
    ) => {
      return contentGridApi.uploadMedia(gridId, rowId, companyId, file, mediaType);
    },
    []
  );

  const migrateFromClient = useCallback(
    async (companyId: number, rows: ContentGridRow[], gridName?: string) => {
      return contentGridApi.migrateFromClient(companyId, rows, gridName);
    },
    []
  );

  const duplicateRow = useCallback(
    async (gridId: number, rowId: string, companyId: number) => {
      return contentGridApi.duplicateRow(gridId, rowId, companyId);
    },
    []
  );

  return {
    loading,
    listGrids,
    loadGrid,
    saveRows,
    createGrid,
    addRowsBatch,
    sendToCalendar,
    createShareLink,
    uploadMedia,
    migrateFromClient,
    duplicateRow,
  };
};

export type { ContentGridPayload, ContentGridSummary };
